/**
 * Cart Cross-Sell Drag Carousel — v8
 *
 * v6: ResizeObserver re-measure so drag/snap math self-heals when the cart
 * drawer switches out of its "is-empty" layout right after an add-to-cart.
 *
 * v7: Fixed the add-to-cart endpoint. The button used
 * `window.Shopify.routes.root`, which this theme never defines; the theme's
 * real flow (product-form.js) uses `window.routes.cart_add_url`.
 *
 * v8: The Add button STILL didn't fire, for a second, independent reason:
 * the button sits inside `.cart-cross-sell__track`, and on `pointerdown`
 * the track called `setPointerCapture()` on itself. Pointer capture
 * retargets subsequent pointer events to the capturing element, and the
 * synthesized `click` is then dispatched against the track rather than the
 * button — so the button's own click listener never ran. `onDragMove` also
 * called `preventDefault()`, which suppresses the click after any tiny
 * finger/mouse movement. Now a pointerdown that starts on the Add button
 * (or a link) never begins a drag and never captures, and the click is
 * handled by delegation on the component so it survives drawer re-renders.
 */
class CartCrossSell extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => {
      this.carousel = this.querySelector('.cart-cross-sell__track');
      this.slides = this.querySelectorAll('.cart-cross-sell__slide');
      this.prevBtn = this.querySelector('.cart-cross-sell__arrow--prev');
      this.nextBtn = this.querySelector('.cart-cross-sell__arrow--next');
      this.dots = this.querySelectorAll('.cart-cross-sell__dot');

      this.totalSlides = this.slides.length;
      if (!this.carousel || this.totalSlides === 0) return;

      this.currentIndex = 0;
      this.dragStartX = 0;
      this.dragCurrentX = 0;
      this.isDragging = false;
      this.dragOffset = 0;
      this.carouselWidth = 0;

      this.measure();

      if (this.totalSlides <= 1) {
        if (this.prevBtn) this.prevBtn.style.display = 'none';
        if (this.nextBtn) this.nextBtn.style.display = 'none';
        const dotsEl = this.querySelector('.cart-cross-sell__dots');
        const arrowsEl = this.querySelector('.cart-cross-sell__arrows');
        if (dotsEl) dotsEl.style.display = 'none';
        if (arrowsEl) arrowsEl.style.display = 'none';
      }

      this.bindEvents();
      this.initAddToCart();
      this.updateUI();
      this.goTo(0, false);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.carousel) return;
          const measuredWidth = this.carousel.clientWidth;
          if (measuredWidth > 0 && measuredWidth !== this.carouselWidth) {
            this.measure();
            this.goTo(this.currentIndex, false);
          }
        });
        this.resizeObserver.observe(this.carousel);
      }
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  measure() {
    this.carouselWidth = this.carousel.clientWidth;
    this.slideWidth = this.slides[0] ? this.slides[0].offsetWidth : this.carouselWidth;
  }

  /** True when a pointer event started on something that must stay clickable. */
  isInteractiveTarget(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('.cart-cross-sell__add-btn, a');
  }

  bindEvents() {
    this.carousel.addEventListener('pointerdown', (e) => this.onDragStart(e));
    this.carousel.addEventListener('pointermove', (e) => this.onDragMove(e));
    this.carousel.addEventListener('pointerup', (e) => this.onDragEnd(e));
    this.carousel.addEventListener('pointerleave', (e) => this.onDragEnd(e));
    this.carousel.addEventListener('pointercancel', (e) => this.onDragEnd(e));

    this.carousel.addEventListener('selectstart', (e) => {
      if (this.isDragging) e.preventDefault();
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
    }

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.currentIndex - 1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.currentIndex + 1, true); }
    });

    window.addEventListener('resize', () => {
      this.measure();
      this.goTo(this.currentIndex, false);
    });
  }

  onDragStart(e) {
    // Never hijack a press that began on the Add button or a product link.
    // Capturing the pointer here is what previously swallowed the click.
    if (this.isInteractiveTarget(e.target)) return;

    if (!this.carouselWidth) this.measure();

    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragOffset = this.getCurrentOffset();
    try { this.carousel.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
    this.carousel.classList.add('dragging');
    this.carousel.style.transition = 'none';
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const dx = e.clientX - this.dragStartX;
    this.dragCurrentX = this.dragOffset + dx;
    const minX = -(this.totalSlides - 1) * this.carouselWidth;
    const maxX = 0;
    let clampedX = this.dragCurrentX;
    if (clampedX > maxX + 50) clampedX = maxX + 50;
    if (clampedX < minX - 50) clampedX = minX - 50;
    this.setTrackPosition(clampedX);
  }

  onDragEnd(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.carousel.classList.remove('dragging');
    try { this.carousel.releasePointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
    this.carousel.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

    const dx = e.clientX - this.dragStartX;
    const threshold = this.carouselWidth * 0.15;

    let newIndex = this.currentIndex;
    if (dx < -threshold) {
      newIndex = Math.min(this.currentIndex + 1, this.totalSlides - 1);
    } else if (dx > threshold) {
      newIndex = Math.max(this.currentIndex - 1, 0);
    } else {
      const currentOffset = this.getCurrentOffset();
      const rawIndex = Math.round(-currentOffset / this.carouselWidth);
      newIndex = Math.max(0, Math.min(rawIndex, this.totalSlides - 1));
    }

    // Remember whether this gesture was a real swipe, so a click that
    // follows a drag doesn't also fire an add-to-cart.
    this.lastDragDistance = Math.abs(dx);
    this.goTo(newIndex, true);
  }

  getCurrentOffset() {
    const style = this.carousel.style.transform;
    const match = style.match(/translateX\(([^)]+)\)/);
    if (match) return parseFloat(match[1]);
    return 0;
  }

  setTrackPosition(x) {
    this.carousel.style.transform = `translateX(${x}px)`;
  }

  goTo(index, animate) {
    if (index < 0) index = 0;
    if (index >= this.totalSlides) index = this.totalSlides - 1;
    this.currentIndex = index;

    const targetX = -index * this.carouselWidth;

    if (animate) {
      this.carousel.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else {
      this.carousel.style.transition = 'none';
    }

    this.setTrackPosition(targetX);
    this.updateUI();
  }

  updateUI() {
    this.updateArrows();
    this.updateDots();
  }

  updateArrows() {
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.totalSlides - 1;
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === this.currentIndex);
    });
  }

  /**
   * Delegated on the component itself (not per-button) so it keeps working
   * after the cart drawer swaps its inner HTML.
   */
  initAddToCart() {
    if (this.addToCartBound) return;
    this.addToCartBound = true;

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-cross-sell__add-btn');
      if (!btn || !this.contains(btn)) return;

      e.preventDefault();
      e.stopPropagation();

      // Ignore the click that terminates an actual swipe.
      if (this.lastDragDistance && this.lastDragDistance > 8) {
        this.lastDragDistance = 0;
        return;
      }
      this.lastDragDistance = 0;

      this.addToCart(btn);
    });
  }

  async addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    if (btn.disabled) return;

    btn.classList.add('loading');
    btn.disabled = true;

    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error((data && (data.description || data.message)) || 'Add to cart failed');
      }

      if (data.sections) {
        const sectionHTML = data.sections['cart-drawer'];
        if (sectionHTML) {
          const html = new DOMParser().parseFromString(sectionHTML, 'text/html');

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            const newItems = html.querySelector('cart-drawer-items');
            const currentItems = document.querySelector('cart-drawer-items');
            if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

            const newFooter = html.querySelector('.drawer__footer');
            const currentFooter = document.querySelector('.drawer__footer');
            if (newFooter && currentFooter) currentFooter.innerHTML = newFooter.innerHTML;

            cartDrawer.classList.toggle('is-empty', data.item_count === 0);
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
          }
        }

        const bubbleHTML = data.sections['cart-icon-bubble'];
        if (bubbleHTML) {
          const bubbleParsed = new DOMParser().parseFromString(bubbleHTML, 'text/html');
          const bubbleSection = bubbleParsed.querySelector('.shopify-section');
          const currentBubble = document.getElementById('cart-icon-bubble');
          if (bubbleSection && currentBubble) currentBubble.innerHTML = bubbleSection.innerHTML;
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item_count: data.item_count }
        }));
      }

      // The drawer footer (containing this carousel) may have just been
      // replaced, so only touch the button if it's still on the page.
      if (btn.isConnected) {
        btn.classList.remove('loading');
        btn.classList.add('added');
        const btnText = btn.querySelector('.btn-text');
        if (btnText) {
          const originalText = btnText.textContent;
          btnText.textContent = '✓';
          setTimeout(() => {
            if (!btn.isConnected) return;
            btnText.textContent = originalText;
            btn.classList.remove('added');
            btn.disabled = false;
          }, 1500);
        } else {
          btn.disabled = false;
        }
      }

    } catch (error) {
      console.error('CartCrossSell add-to-cart failed:', error);
      if (btn.isConnected) {
        btn.classList.remove('loading');
        btn.disabled = false;
        const btnText = btn.querySelector('.btn-text');
        if (btnText) {
          const originalText = btnText.textContent;
          btnText.textContent = 'Retry';
          setTimeout(() => {
            if (btn.isConnected) btnText.textContent = originalText;
          }, 1600);
        }
      }
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
